import React, { useState, useEffect} from "react";
import firebase from "gatsby-plugin-firebase";
import { Layout, Card, Form, Input, Modal, Button, Typography, Menu, Tooltip, Row, Col, Popconfirm, message } from "antd";
import { MailOutlined, LoadingOutlined, LockOutlined } from "@ant-design/icons";
import { getEmailStatus, createMigrateUser } from "../services/api";
import { navigate } from "gatsby";
import banner from "../images/logoWorkClass.png";
import * as Sentry from "@sentry/react";


Sentry.init({
	dsn: "https://ceb44551b388485eb468ffb0b3813eb9@notmyfault.workclass.co/3",
});


const { Paragraph } = Typography;

const ItemController = ({ state, visibleStates, children }: { state: string, visibleStates: string[], children: React.ReactNode }): React.FC => {
	if (!visibleStates.includes(state)) return null;
	return (
		<>
			{children}
		</>
	);
};

const EmailInput = ({ 
	setState,
	setEmail,
	attemptMigrate,
	setModalVisible
}: {
	setState: (state: string) => void,
	setEmail: (email: string) => void,
	attemptMigrate: (email: string) => boolean,
	setModalVisible: (visible: boolean) => void
}): React.FC => {
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);
	const [tooltipVisible, setTooltipVisible] = useState<boolean>(true);

	const handleEnterEmail = (e) => {
		const email = e.currentTarget.value;
		if (!email.match(/^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/)) {
			setError(true);
			return
		}
		setLoading(true);
		(async () => {
			const emailStatus = await getEmailStatus(email);
			if (emailStatus === null) {
				message.error("Failed to contact server, please try again");
			} else if (emailStatus === "employer") {
				setState("password");
			} else if (emailStatus === "unmigrated") {
				const success = await attemptMigrate(email);
				if (success) {
					setModalVisible(true);
					setState("password");
				} else {
					message.error("Failed to contact server, please try again")
				}
			} else if (emailStatus === "jobseeker") {
				navigate("https://workclass.co");
			} else if (emailStatus === "none") {
				setState("register");
			}
			setEmail(email);
			setLoading(false);
		})();
	};

	return (
		<Form.Item
			validateStatus={error ? "error" : "success"}
			help={error ? "Please enter a valid email address." : null}
		>
			<Tooltip
				title="Enter your email to login or register"
				placement="bottomLeft"
				visible={tooltipVisible}
				color="rgba(0, 0, 0, 0.5)"
			>
			<Input 
				autoFocus
				disabled={loading}
				prefix={<MailOutlined style={{marginRight: 5}} />}
				suffix={loading ? <LoadingOutlined /> : null}
				style={{width: 400}}
				size="large"
				placeholder="Email"
				onPressEnter={handleEnterEmail}
				onChange={(e) => {
					setTooltipVisible(e.currentTarget.value.length === 0);
					setError(false);
					setState("email");
				}}
			/>
			</Tooltip>
		</Form.Item>
	);
};


const PasswordInput = ({ attemptLogin, disabled }: { attemptLogin: (password: string) => void, disabled: boolean }): React.FC => {
	const [error, setError] = useState<boolean>(false);

	const handleEnterPassword = (e) => {
		const password = e.currentTarget.value;
		(async () => {
			const success = await attemptLogin(password);
			if (!success) {
				setError(true);
			}
		})();
	};

	return (
		<Form.Item
			validateStatus={error ? "error" : "success"}
			name="loginPassword"
			help={error ? "The email/password combination is invalid." : null}
		>
			<Input
				autoFocus
				disabled={disabled}
				prefix={<LockOutlined style={{marginRight: 5}} />}
				suffix={disabled ? <LoadingOutlined /> : null}
				style={{width: 400}}
				size="large"
				type="password"
				placeholder="Password"
				onPressEnter={handleEnterPassword}
				onChange={() => setError(false)}
			/>
		</Form.Item>
	);
};

const RegisterInput = ({ attemptRegister, disabled }: { attemptRegister: (password: string) => boolean, disabled: boolean }): React.FC => {
	const [error, setError] = useState<boolean>(false);
	const [password, setPassword] = useState<string>("");

	const handleEnterRegister = (e) => {
		if (disabled) return;
		const confirmation = e.currentTarget.value;
		if (password !== confirmation) {
			setError(true);
		} else {
			(async () => {
				const success = await attemptRegister(password);
				if (!success) {
					message.error("Registration failed");
				}
			})();
		}
	};

	return (
		<>
		<Form.Item
			name="registerPassword"
			validateStatus={error ? "error": "success"}
			help={error ? "The passwords do not match." : null}
		>
			<Input
				autoFocus
				disabled={disabled}
				prefix={<LockOutlined style={{marginRight: 5}} />}
				suffix={disabled ? <LoadingOutlined /> : null}
				style={{width: 400}}
				size="large"
				type="password"
				placeholder="Password"
				onChange={(e) => {
					setError(false);
					setPassword(e.currentTarget.value);
				}}
			/>
		</Form.Item>
		<Form.Item
			name="confirmPassword"
			validateStatus={error ? "error" : "success"}
		>
			<Input
				disabled={disabled}
				prefix={<LockOutlined style={{marginRight: 5}} />}
				suffix={disabled ? <LoadingOutlined /> : null}
				style={{width: 400}}
				size="large"
				type="password"
				placeholder="Confirm password"
				onPressEnter={handleEnterRegister}
				onChange={() => setError(false)}
			/>
		</Form.Item>
		</>
	);
}

const LoginPage = (): React.FC => {
	const [form] = Form.useForm();
	const [state, setState] = useState<string>("email");
	const [email, setEmail] = useState<string>(null);
	const [modalVisible, setModalVisible] = useState<boolean>(false);
	const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);
	const [passwordDisabled, setPasswordDisabled] = useState<boolean>(false);

	useEffect(() => {
		firebase.auth().onAuthStateChanged((user) => {
			if (user) {
				navigate("/dashboard");
			}
		});
	}, [])

	const attemptMigrate = async (email: string): boolean => {
		try {
			const result = await createMigrateUser(email);
			if (result) {
				await firebase.auth().sendPasswordResetEmail(email, {url: window.location.href});
				return true;
			} else {
				return false;
			}
		} catch {
			return false;
		}
	};
	const attemptLogin = async (password: string): boolean => {
		setButtonsDisabled(true);
		setPasswordDisabled(true);
		try {
			await firebase.auth().signInWithEmailAndPassword(email, password);
			setPasswordDisabled(false);
			return true;
		}
		catch {
			setButtonsDisabled(false);
			setPasswordDisabled(false);
			return false;
		}
	};
	const attemptRegister = async (password: string): boolean => {
		setPasswordDisabled(true);
		setButtonsDisabled(true);
		try {
			await firebase.auth().createUserWithEmailAndPassword(email, password);
			setButtonsDisabled(false);
			setPasswordDisabled(false);
			return true;
		} catch {
			setButtonsDisabled(false);
			setPasswordDisabled(false);
			return false;
		}
	}
	const resetPassword = async (): void => {
		setPasswordDisabled(true);
		setButtonsDisabled(true);
		try {
			await firebase.auth().sendPasswordResetEmail(email, {url: window.location.href});
			message.success("We've sent a reset link to your email address.");
		} catch {
			message.error("Failed to contact server, please try again");
		}
		setPasswordDisabled(false);
		setButtonsDisabled(false);
	};

	let buttons = null;
	switch (state) {
		case "password":
			buttons = (
				<Row>
				<Col span={12}>
					<Popconfirm
						title="Send a password reset email?"
						onConfirm={resetPassword}
						okText="Yes"
						cancelText="No"
					>
					<Button disabled={buttonsDisabled} style={{marginTop: 30, marginBottom: -20}}>Forgot Password</Button> 
					</Popconfirm>
				</Col>
				<Col span={12}>
					<Button disabled={buttonsDisabled} onClick={() => attemptLogin(form.getFieldsValue().loginPassword)} style={{marginTop: 30, marginBottom: -20, width: 134.73}} type="primary">Login</Button>
				</Col>
				</Row>
			);
			break;
		case "register":
			buttons = (
				<Row>
				<Col span={12}>
				</Col>
				<Col span={12}>
					<Button disabled={buttonsDisabled} onClick={() => attemptRegister(form.getFieldsValue().registerPassword)} style={{marginTop: 30, marginBottom: -20, width: 134.73}} type="primary">Register</Button>
				</Col>
				</Row>
			);
	}

	return (
		<Layout>
			<div style={{height: "100vh"}}>
			<div style={{position: "fixed", top: "25%", display: "flex", justifyContent: "center", alignItems: "center", width: "100vw"}}>
				<Card style={{boxShadow: "0 0.5px 3px #CCC", textAlign: "center"}} cover={<img src={banner} style={{width: 450, padding: 20, paddingBottom: 0}} />}>
				<Menu mode="horizontal" selectedKeys={["employer"]} style={{marginTop: -24, marginBottom: 24, textAlign: "center", fontSize: 20}}>
					<Menu.Item key="employer">
						Employer
					</Menu.Item>
					<Menu.Item onClick={() => navigate("https://workclass.co")}>
						Jobseeker
					</Menu.Item>
				</Menu>
					<Form style={{marginBottom: -24}} form={form}>
						<ItemController state={state} visibleStates={["email", "password", "register"]}>
							<EmailInput setEmail={setEmail} setState={setState} attemptMigrate={attemptMigrate} setModalVisible={setModalVisible} />
						</ItemController>
						<ItemController state={state} visibleStates={["password"]}>
							<PasswordInput attemptLogin={attemptLogin} disabled={passwordDisabled} />
						</ItemController>
						<ItemController state={state} visibleStates={["register"]}>
							<RegisterInput attemptRegister={attemptRegister} disabled={passwordDisabled} />
						</ItemController>
					</Form>
					{buttons}
				</Card>
			</div>
			</div>

			<Modal
				visible={modalVisible}
				title="Check your email"
				onCancel={() => setModalVisible(false)}
				footer={[
					<Button key={0} onClick={() => setModalVisible(false)}>Ok</Button>
				]}
			>
				<Paragraph>{`We've sent an email to you. Follow the link to set your password, then come back to this page to log in.`}</Paragraph>
			</Modal>
		</Layout>
	);
};

export default LoginPage;